<template>
  <SectionPageShell section="invoices" title="Rechnungen">
    <section class="invoice-overview mb-4">
      <h2 class="invoice-overview__title">Aktuell offene Rechnungen</h2>

      <div v-if="invoicesStore.overviewLoading" class="text-muted">
        Lade Rechnungen...
      </div>
      <template v-else>
        <div v-if="!hasOpenInvoices" class="invoice-overview__settled">
          <Icon icon="fa-pro-solid:circle-check" />
          <span>Alle Rechnungen sind beglichen.</span>
        </div>

        <article
          v-for="invoice in summaryInvoices"
          :key="invoice.id"
          class="invoice-latest"
        >
          <div class="invoice-latest__header">
            <div>
              <span class="invoice-latest__eyebrow">
                {{ hasOpenInvoices ? 'Offene Rechnung' : 'Letzte Rechnung' }}
              </span>
              <h3 class="invoice-latest__title">
                {{ invoiceTitle(invoice) }}
              </h3>
            </div>
            <a
              v-if="invoice.pdf_url"
              :href="invoice.pdf_url"
              target="_blank"
              rel="noopener noreferrer"
              class="btn btn-warning"
            >
              PDF öffnen
            </a>
          </div>

          <div class="invoice-latest__grid">
            <div class="invoice-latest__amount">
              <span>Betrag</span>
              <PriceAmount :amount="invoice.gross_total" prominent />
            </div>
            <dl class="invoice-latest__details">
              <div>
                <dt>Nummer</dt>
                <dd>{{ formatInvoiceNumber(invoice.number) }}</dd>
              </div>
              <div>
                <dt>Datum</dt>
                <dd>{{ formatDateValue(invoice.invoice_date) }}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span class="badge" :class="statusClass(invoice)">
                    {{ formatStatus(invoice.invoice_status_id) }}
                  </span>
                </dd>
              </div>
              <div>
                <dt>Fälligkeit</dt>
                <dd>{{ formatDueDate(invoice) }}</dd>
              </div>
              <div v-if="invoice.date_of_payment">
                <dt>Bezahlt am</dt>
                <dd>{{ formatDateValue(invoice.date_of_payment) }}</dd>
              </div>
              <div v-if="invoice.client">
                <dt>Kunde</dt>
                <dd>{{ invoice.client.name }} · {{ invoice.client.kdnr }}</dd>
              </div>
            </dl>
          </div>
        </article>
      </template>
    </section>

    <section class="invoice-search mb-4">
      <div class="row g-3 align-items-end">
        <div class="col-12 col-lg-8">
          <label for="invoice-search-range" class="form-label">Rechnungszeitraum</label>
          <DaterangePicker id="invoice-search-range" v-model="dateRange" placeholder="Zeitraum auswählen" />
        </div>
        <div class="col-12 col-lg-4">
          <button
            type="button"
            class="btn btn-primary invoice-search__button"
            :disabled="!canSearch || invoicesStore.loading"
            @click="searchInvoices"
          >
            Rechnungen suchen
          </button>
        </div>
      </div>
    </section>

    <DataTable
      v-if="searchPerformed"
      :value="invoicesStore.items"
      :rows="invoicesStore.serverOptions.rows"
      :totalRecords="invoicesStore.totalRecords"
      :loading="invoicesStore.loading"
      :lazy="true"
      :sortField="invoicesStore.serverOptions.sortField"
      :sortOrder="invoicesStore.serverOptions.sortOrder"
      paginator
      removableSort
      dataKey="id"
      paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      :rowsPerPageOptions="[20,50,100]"
      currentPageReportTemplate="Zeige {first} bis {last} von {totalRecords}"
      responsiveLayout="scroll"
      @page="onPage"
      @sort="onSort"
    >
      <template #empty>
        Keine Rechnungen gefunden.
      </template>

      <TableColumn field="number" header="Nummer" sortable>
        <template #body="{ data }">
          {{ formatInvoiceNumber(data.number) }}
        </template>
      </TableColumn>

      <TableColumn field="invoice_date" header="Rechnungsdatum" sortable>
        <template #body="{ data }">
          {{ formatDateValue(data.invoice_date) }}
        </template>
      </TableColumn>

      <TableColumn field="invoice_status_id" header="Status" sortable>
        <template #body="{ data }">
          {{ formatStatus(data.invoice_status_id) }}
        </template>
      </TableColumn>

      <TableColumn field="gross_total" header="Betrag" sortable>
        <template #body="{ data }">
          <PriceAmount :amount="data.gross_total" inline />
        </template>
      </TableColumn>

      <TableColumn header="">
        <template #body="{ data }">
          <a
            v-if="data.pdf_url"
            :href="data.pdf_url"
            target="_blank"
            rel="noopener noreferrer"
            class="d-inline-flex align-items-center"
            title="PDF herunterladen"
          >
            <ActionIcon icon="fa-pro-solid:file-arrow-down" />
          </a>
        </template>
      </TableColumn>
    </DataTable>
    <div v-else class="invoice-empty-state">
      Wählen Sie einen Zeitraum aus und starten Sie die Suche, um Rechnungen anzuzeigen.
    </div>
  </SectionPageShell>
</template>

<script>
import ActionIcon from "@/components/general/ActionIcon.vue";
import DaterangePicker from "@/components/DaterangePicker.vue";
import SectionPageShell from "@/components/base_type/SectionPageShell.vue";
import PriceAmount from "@/components/general/PriceAmount.vue";
import { useInvoicesStore } from "@/stores/invoices";
import dayjs from "dayjs";

export default {
  name: "AccountInvoices",
  components: {
    ActionIcon,
    DaterangePicker,
    PriceAmount,
    SectionPageShell,
  },
  setup() {
    return {
      invoicesStore: useInvoicesStore(),
    }
  },
  data() {
    return {
      dateRange: {
        from: dayjs().startOf('year').valueOf(),
        to: dayjs().valueOf(),
      },
      searchPerformed: false,
    }
  },
  created() {
    this.invoicesStore.items = []
    this.invoicesStore.totalRecords = 0
    this.invoicesStore.fetchOverview()
  },
  computed: {
    hasOpenInvoices() {
      return this.invoicesStore.openInvoices.length > 0
    },
    latestInvoice() {
      return this.invoicesStore.latestInvoice
    },
    summaryInvoices() {
      if (this.hasOpenInvoices) {
        return this.invoicesStore.openInvoices
      }

      return this.latestInvoice ? [this.latestInvoice] : []
    },
    canSearch() {
      return !!this.invoiceSearchFilters.invoiceDateFrom && !!this.invoiceSearchFilters.invoiceDateTo
    },
    invoiceSearchFilters() {
      return {
        invoiceDateFrom: this.normalizeDateRangeValue(this.dateRange?.from),
        invoiceDateTo: this.normalizeDateRangeValue(this.dateRange?.to),
      }
    },
  },
  methods: {
    onPage(event) {
      this.invoicesStore.serverOptions = {
        ...this.invoicesStore.serverOptions,
        ...event,
      }
      this.invoicesStore.index(this.invoiceSearchFilters)
    },
    onSort(event) {
      this.invoicesStore.serverOptions = {
        ...this.invoicesStore.serverOptions,
        sortField: event.sortField || 'number',
        sortOrder: event.sortOrder || -1,
      }
      this.invoicesStore.index(this.invoiceSearchFilters)
    },
    searchInvoices() {
      if (!this.canSearch) {
        return
      }

      this.searchPerformed = true
      this.invoicesStore.serverOptions = {
        ...this.invoicesStore.serverOptions,
        page: 0,
      }
      this.invoicesStore.index(this.invoiceSearchFilters)
    },
    normalizeDateRangeValue(value) {
      if (!value) {
        return null
      }

      const date = value.dateInstance ? value.dateInstance : value
      return dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : null
    },
    formatDateValue(value) {
      return value ? dayjs(value).format('DD.MM.YYYY') : '-'
    },
    formatInvoiceNumber(value) {
      if (!value) {
        return '-'
      }

      return /^\d+$/.test(value) ? `R${value}` : value
    },
    invoiceTitle(invoice) {
      return invoice.number ? this.formatInvoiceNumber(invoice.number) : 'Rechnung'
    },
    formatDueDate(invoice) {
      if (invoice.overdue) {
        return `${Math.abs(invoice.days_after_due_date || 0)} Tage überfällig`
      }

      if (typeof invoice.days_after_due_date === 'number' && invoice.days_after_due_date < 0) {
        return `fällig in ${Math.abs(invoice.days_after_due_date)} Tagen`
      }

      return 'nicht überfällig'
    },
    formatStatus(status) {
      const labels = {
        1: 'Entwurf',
        2: 'Fixiert',
        3: 'gesendet',
        4: 'bezahlt',
        5: 'erinnert',
        6: 'gemahnt',
        7: 'Übergabe RA',
        8: 'storniert',
        9: 'ausgebucht',
        10: 'bezahlt',
      }

      return labels[status] || String(status ?? '-')
    },
    statusClass(invoice) {
      if (invoice.overdue) {
        return 'bg-danger'
      }

      if (invoice.invoice_status_id === 4 || invoice.invoice_status_id === 10) {
        return 'bg-success'
      }

      return 'bg-secondary'
    },
  },
}
</script>

<style scoped lang="scss">
.invoice-search {
  background: #ffffff;
  border: 1px solid #dce4ec;
  padding: 1.25rem;
}

.invoice-overview__title {
  margin: 0 0 1rem;
  color: #173955;
  font-size: 1.35rem;
}

.invoice-overview__settled {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 1rem 1.25rem;
  color: #246b45;
  background: #eef9f2;
  border: 1px solid #b9dec7;
}

.invoice-overview__settled .iconify {
  flex: 0 0 auto;
  font-size: 1.4rem;
}

.invoice-latest {
  margin-top: 1rem;
  padding: 1.25rem;
  background: #ffffff;
  border: 1px solid #dce4ec;
}

.invoice-latest__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.invoice-latest__eyebrow {
  display: block;
  color: #6b7f92;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.invoice-latest__title {
  margin: 0.2rem 0 0;
  color: #173955;
  font-size: 1.35rem;
  line-height: 1.25;
}

.invoice-latest__grid {
  display: grid;
  grid-template-columns: minmax(12rem, 0.35fr) 1fr;
  gap: 1.25rem;
}

.invoice-latest__amount {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.35rem;
  padding: 1rem;
  background: #f7fafc;
  border-radius: 6px;
}

.invoice-latest__amount > span {
  color: #6b7f92;
  font-size: 0.85rem;
  font-weight: 700;
}

.invoice-latest__details {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin: 0;
}

.invoice-latest__details div {
  min-width: 0;
}

.invoice-latest__details dt {
  color: #6b7f92;
  font-size: 0.78rem;
  font-weight: 700;
}

.invoice-latest__details dd {
  margin: 0.25rem 0 0;
  color: #173955;
  overflow-wrap: anywhere;
}

.invoice-search__button {
  width: 100%;
}

.invoice-empty-state {
  padding: 1rem;
  color: #6b7f92;
  background: #f7fafc;
  border: 1px dashed #c7d2de;
}

@media (max-width: 991.98px) {
  .invoice-latest__header,
  .invoice-latest__grid {
    display: block;
  }

  .invoice-latest__header .btn {
    margin-top: 1rem;
  }

  .invoice-latest__amount {
    margin-bottom: 1rem;
  }

  .invoice-latest__details {
    grid-template-columns: 1fr;
  }
}
</style>
